import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { exportPaymentReport, getPaymentReport } from "../../api/reports";
import { formatINR, sumBy } from "./reportUtils";

const formatPaymentDate = (value) => {
  if (!value) return "—";

  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00+05:30` : value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const PaymentGatewayReport = () => {
  const [, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    q: "",
    page: 1,
    limit: 10,
    sortBy: "status",
    sortOrder: "desc",
    tab: "payments",
    method: "",
    status: "",
  });

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchPaymentReport = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getPaymentReport(filters);
        if (cancelled) return;

        const reportRows = response?.data?.rows || response?.rows || [];
        setRows(reportRows);
        setReportTotals(response?.data?.totals || response?.totals || {});
        setPagination(
          response?.pagination || {
            page: filters.page,
            limit: filters.limit,
            total: reportRows.length,
            totalPages: 1,
          },
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRows([]);
        setReportTotals({});
        setPagination({ page: 1, limit: filters.limit, total: 0, totalPages: 1 });
        setError(
          fetchError?.response?.data?.message ||
            fetchError.message ||
            "Failed to load payment report.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPaymentReport();
    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.tab,
    filters.method,
    filters.status,
  ]);

  const totals = {
    amount: {
      value: reportTotals.amount ?? sumBy(rows, "amount"),
      format: "inr",
    },
    fee: {
      value: reportTotals.fee ?? sumBy(rows, "fee"),
      format: "inr",
    },
    feeTax: {
      value: reportTotals.feeTax ?? sumBy(rows, "feeTax"),
      format: "inr",
    },
  };

  const availableColumns = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
    },
    {
      key: "paymentId",
      label: "Payment ID",
      sortable: true,
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (row) => formatPaymentDate(row.date),
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => formatINR(row.amount),
    },
    {
      key: "fee",
      label: "Fee",
      sortable: true,
      render: (row) => formatINR(row.fee),
    },
    {
      key: "feeTax",
      label: "Fee Tax",
      sortable: true,
      render: (row) => formatINR(row.feeTax),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => <span>{row.status}</span>,
    },
    { key: "settlementId", label: "Settlement ID", sortable: true },
    { key: "utr", label: "UTR", sortable: true },
    {
      key: "settledDate",
      label: "Settled Date",
      sortable: true,
      render: (row) => formatPaymentDate(row.settledDate),
    },
    { key: "refundId", label: "Refund ID", sortable: true },
    { key: "refundStatus", label: "Refund Status", sortable: true },
    {
      key: "refundAmount",
      label: "Refund Amount",
      sortable: true,
      render: (row) => formatINR(row.refundAmount),
    },
  ];
  const columns = availableColumns.filter((column) =>
    rows.some((row) => {
      const value = row[column.key];
      return value !== null && value !== undefined && value !== "";
    }),
  );

  const handleExport = async () => {
    setExporting(true);
    setError("");

    try {
      const response = await exportPaymentReport({
        from: filters.from,
        to: filters.to,
        status: filters.status,
        q: filters.q,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        method: filters.method,
        tab: filters.tab,
      });
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers?.["content-disposition"];
      const fileNameMatch = disposition?.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/"/g, ""))
        : "payment-gateway-report.csv";

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(
        exportError?.response?.data?.message ||
          exportError.message ||
          "Failed to export payment report.",
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-fluid p-4 report-page">
      <div className="card p-3">
        <ReportFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={setFilters}
          searchPlaceholder="Search order or payment ID..."
          filterSlotInline
          actionSlot={
            <button
              type="button"
              className="btn btn-success w-100"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          }
          filterSlot={
            <>
              <div className="col-12 col-lg-2">
                <label htmlFor="payment-status" className="form-label">
                  Status
                </label>

                <select
                  id="payment-status"
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All Status</option>
                  <option value="PAYMENT_PENDING">Payment Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="CAPTURED">Captured</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>
            </>
          }
        />

        <ReportTable
          columns={columns}
          rows={rows}
          totals={totals}
          loading={loading}
          error={error}
          page={filters.page}
          total={pagination.total ?? rows.length}
          totalPages={pagination.totalPages || 1}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={(sortBy, sortOrder) =>
            setFilters((prev) => ({
              ...prev,
              sortBy,
              sortOrder,
              page: 1,
            }))
          }
          onPageChange={(page) =>
            setFilters((prev) => ({
              ...prev,
              page: Math.min(Math.max(page, 1), pagination.totalPages || 1),
            }))
          }
          rowKey="paymentId"
        />
      </div>

      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default PaymentGatewayReport;
